import { useEffect, useMemo, useRef, useState } from "react";
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Crosshair,
  Maximize2,
  Minus,
  Plus,
} from "react-feather";
import * as Konva from "react-konva";
import clsx from "clsx";
import { Barcode } from "@bfc-software/pallet";

import { getPalette } from "./palette";
import {
  INITIAL_SLIDER_STATE,
  nextSliderState,
  type SliderState,
} from "./slider-state";
import type { SimModel, SimPick, SimTask } from "./simulator-model";
import styles from "./PlanSimulator.module.css";
import { Trucks } from "./Trucks";
import {
  computeFollowView,
  computeWarehouseLayout,
  fitView,
  MAX_SCALE,
  MIN_SCALE,
  Warehouse,
  type ViewState,
} from "./Warehouse";

export type SimulatorProps = {
  model: SimModel;
};

const TRUCKS_PANE_WIDTH = 248;
const TASK_SLIDER_WIDTH = 72;
const LEGEND_HEIGHT = 32;
const BUTTON_ZOOM_FACTOR = 1.25;

function useElementSize<T extends HTMLElement>() {
  const ref = useRef<T | null>(null);
  const [size, setSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const observer = new ResizeObserver(([entry]) => {
      if (!entry) return;
      const { width, height } = entry.contentRect;
      setSize({ width, height });
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return [ref, size] as const;
}

export function PlanSimulator({ model }: SimulatorProps) {
  const [stageRef, stageSize] = useElementSize<HTMLDivElement>();

  const [slider, setSlider] = useState<SliderState>(INITIAL_SLIDER_STATE);
  const taskCount = model.tasks.length;
  const activeTask = model.tasks[slider.taskIndex];
  const detailCount = activeTask?.picks.length ?? 0;
  const activePick = activeTask?.picks[slider.detailIndex];

  const onSliderChange = (kind: "task" | "detail", value: number) => {
    setSlider((prev) => nextSliderState(model, prev, { kind, value }));
  };

  const warehousePaneWidth = Math.max(
    0,
    stageSize.width - TRUCKS_PANE_WIDTH - TASK_SLIDER_WIDTH,
  );
  const warehousePaneHeight = Math.max(0, stageSize.height - LEGEND_HEIGHT);

  const warehouseLayout = useMemo(
    () => computeWarehouseLayout(model.warehouseGrid.slots),
    [model.warehouseGrid.slots],
  );

  const [view, setView] = useState<ViewState>({ scale: 1, x: 0, y: 0 });
  const [hasUserAdjustedView, setHasUserAdjustedView] = useState(false);
  const [followOn, setFollowOn] = useState(true);

  const viewRef = useRef(view);
  viewRef.current = view;
  const followAnimRef = useRef<number | null>(null);
  const followPaneRef = useRef({ w: 0, h: 0 });

  const activePlaced = activePick
    ? warehouseLayout.bySlot.get(activePick.slot)
    : undefined;
  const activeRow = activePlaced?.row;
  const activeCol = activePlaced?.col;

  // Auto-fit until the user interacts; afterwards, pane resizes don't clobber
  // their view. The Fit button re-arms auto-fit. Follow takes over when on.
  useEffect(() => {
    if (followOn) return;
    if (hasUserAdjustedView) return;
    if (warehousePaneWidth <= 0 || warehousePaneHeight <= 0) return;
    setView(
      fitView(
        warehousePaneWidth,
        warehousePaneHeight,
        warehouseLayout.worldWidth,
        warehouseLayout.worldHeight,
      ),
    );
  }, [
    followOn,
    hasUserAdjustedView,
    warehousePaneWidth,
    warehousePaneHeight,
    warehouseLayout.worldWidth,
    warehouseLayout.worldHeight,
  ]);

  // Follow: tween the warehouse view so the active cell stays centred.
  // Active-cell-unchanged scrubs naturally skip (effect doesn't re-fire);
  // pane resizes snap without animation so they don't read as a jump.
  useEffect(() => {
    if (!followOn) return;
    if (activeRow == null || activeCol == null) return;
    if (warehousePaneWidth <= 0 || warehousePaneHeight <= 0) return;

    const target = computeFollowView(
      warehousePaneWidth,
      warehousePaneHeight,
      activeRow,
      activeCol,
    );

    const paneChanged =
      followPaneRef.current.w !== warehousePaneWidth ||
      followPaneRef.current.h !== warehousePaneHeight;
    followPaneRef.current = { w: warehousePaneWidth, h: warehousePaneHeight };

    if (followAnimRef.current != null) {
      cancelAnimationFrame(followAnimRef.current);
      followAnimRef.current = null;
    }

    if (paneChanged) {
      setView(target);
      return;
    }

    const startView = viewRef.current;
    const startTime = performance.now();
    const easeInOut = (t: number) =>
      t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
    const tick = (now: number) => {
      const t = Math.min(1, (now - startTime) / 240);
      const e = easeInOut(t);
      setView({
        scale: startView.scale + (target.scale - startView.scale) * e,
        x: startView.x + (target.x - startView.x) * e,
        y: startView.y + (target.y - startView.y) * e,
      });
      if (t < 1) {
        followAnimRef.current = requestAnimationFrame(tick);
      } else {
        followAnimRef.current = null;
      }
    };
    followAnimRef.current = requestAnimationFrame(tick);

    return () => {
      if (followAnimRef.current != null) {
        cancelAnimationFrame(followAnimRef.current);
        followAnimRef.current = null;
      }
    };
  }, [followOn, activeRow, activeCol, warehousePaneWidth, warehousePaneHeight]);

  const handleZoomButton = (factor: number) => {
    const cx = warehousePaneWidth / 2;
    const cy = warehousePaneHeight / 2;
    setView((prev) => {
      const newScale = Math.max(
        MIN_SCALE,
        Math.min(MAX_SCALE, prev.scale * factor),
      );
      if (newScale === prev.scale) return prev;
      const worldX = (cx - prev.x) / prev.scale;
      const worldY = (cy - prev.y) / prev.scale;
      return {
        scale: newScale,
        x: cx - worldX * newScale,
        y: cy - worldY * newScale,
      };
    });
    setHasUserAdjustedView(true);
    setFollowOn(false);
  };

  const handleFit = () => {
    setFollowOn(false);
    setHasUserAdjustedView(false);
    setView(
      fitView(
        warehousePaneWidth,
        warehousePaneHeight,
        warehouseLayout.worldWidth,
        warehouseLayout.worldHeight,
      ),
    );
  };

  const handleFollowToggle = () => {
    setFollowOn((prev) => !prev);
  };

  const handleUserAdjust = () => {
    setHasUserAdjustedView(true);
    setFollowOn(false);
  };

  return (
    <div
      data-test-role="plan-simulator"
      data-active-task-id={activeTask?.id}
      data-active-pick-id={activePick?.id}
      className={styles.root}
    >
      <div className={styles.stageCard}>
        <div
          className={styles.stage}
          data-test-role="plan-simulator-stage"
          ref={stageRef}
        >
          {stageSize.width > 0 && stageSize.height > 0 ? (
            <Konva.Stage width={stageSize.width} height={stageSize.height}>
              <Konva.Layer>
                <Trucks
                  trucks={model.trucks}
                  activeTask={activeTask}
                  activePick={activePick}
                  x={16}
                  y={16}
                  width={TRUCKS_PANE_WIDTH - 32}
                  height={stageSize.height - 32}
                />
                <Warehouse
                  layout={warehouseLayout}
                  activeTask={activeTask}
                  activePick={activePick}
                  x={TRUCKS_PANE_WIDTH}
                  y={LEGEND_HEIGHT}
                  width={warehousePaneWidth}
                  height={warehousePaneHeight}
                  view={view}
                  onViewChange={setView}
                  onUserAdjust={handleUserAdjust}
                />
              </Konva.Layer>
            </Konva.Stage>
          ) : null}
          <Legend />
          <div
            className={styles.zoomControls}
            data-test-role="plan-simulator-zoom-controls"
          >
            <button
              type="button"
              className={`${styles.zoomButton} ${
                followOn ? styles.zoomButtonActive : ""
              }`}
              aria-label="Follow active slot"
              aria-pressed={followOn}
              title="Follow active slot"
              data-test-role="warehouse-follow-toggle"
              onClick={handleFollowToggle}
            >
              <Crosshair size={14} />
            </button>
            <button
              type="button"
              className={styles.zoomButton}
              aria-label="Zoom out"
              data-test-role="warehouse-zoom-out"
              onClick={() => handleZoomButton(1 / BUTTON_ZOOM_FACTOR)}
            >
              <Minus size={14} />
            </button>
            <button
              type="button"
              className={styles.zoomButton}
              aria-label="Fit warehouse"
              data-test-role="warehouse-zoom-fit"
              onClick={handleFit}
            >
              <Maximize2 size={14} />
            </button>
            <button
              type="button"
              className={styles.zoomButton}
              aria-label="Zoom in"
              data-test-role="warehouse-zoom-in"
              onClick={() => handleZoomButton(BUTTON_ZOOM_FACTOR)}
            >
              <Plus size={14} />
            </button>
          </div>
          <div
            className={styles.paneDivider}
            // eslint-disable-next-line react/forbid-dom-props -- follow-up: replace hardcoded inline style with flexbox/pallet utilities
            style={{ left: TRUCKS_PANE_WIDTH }}
            aria-hidden="true"
          />
        </div>
        <TaskSlider
          taskCount={taskCount}
          taskIndex={slider.taskIndex}
          onChange={(v) => onSliderChange("task", v)}
        />
        <DetailSlider
          detailCount={detailCount}
          detailIndex={slider.detailIndex}
          onChange={(v) => onSliderChange("detail", v)}
        />
      </div>
      <Readout
        model={model}
        activeTask={activeTask}
        activePick={activePick}
        detailCount={detailCount}
      />
    </div>
  );
}

function Legend() {
  const palette = getPalette();
  return (
    <div className={styles.legend} aria-hidden="true">
      <span className={styles.legendItem}>
        <span
          className={styles.legendDot}
          // eslint-disable-next-line react/forbid-dom-props -- runtime-computed value
          style={{ backgroundColor: palette.markerActive }}
        />
        Active pallet
      </span>
      <span className={styles.legendItem}>
        <span
          className={styles.legendDot}
          // eslint-disable-next-line react/forbid-dom-props -- runtime-computed value
          style={{ backgroundColor: palette.markerInTask }}
        />
        In task
      </span>
      <span className={styles.legendItem}>
        <span
          className={styles.legendDot}
          // eslint-disable-next-line react/forbid-dom-props -- runtime-computed value
          style={{ backgroundColor: palette.markerUnused }}
        />
        Unused
      </span>
    </div>
  );
}

type TaskSliderProps = {
  taskCount: number;
  taskIndex: number;
  onChange: (value: number) => void;
};

function TaskSlider({ taskCount, taskIndex, onChange }: TaskSliderProps) {
  const disabled = taskCount === 0;
  const max = Math.max(taskCount - 1, 0);
  return (
    <div
      className={styles.taskSlider}
      data-test-role="plan-simulator-task-slider"
    >
      <span className={styles.sliderLabel}>TASK</span>
      <button
        type="button"
        className={styles.taskStepButton}
        aria-label="Next task"
        disabled={disabled || taskIndex >= max}
        onClick={() => onChange(taskIndex + 1)}
      >
        <ChevronUp size={16} />
      </button>
      <div className={styles.taskTrackWrap}>
        <div className={styles.taskTicks} aria-hidden="true">
          {Array.from({ length: taskCount }, (_, i) => (
            <span key={i} className={styles.taskTick} />
          ))}
        </div>
        <input
          type="range"
          className={styles.taskRange}
          aria-label="Task"
          min={0}
          max={max}
          step={1}
          value={taskIndex}
          disabled={disabled}
          onChange={(e) => onChange(e.target.valueAsNumber)}
        />
      </div>
      <button
        type="button"
        className={styles.taskStepButton}
        aria-label="Previous task"
        disabled={disabled || taskIndex <= 0}
        onClick={() => onChange(taskIndex - 1)}
      >
        <ChevronDown size={16} />
      </button>
      <span className={styles.sliderValue}>
        {taskCount === 0 ? "—" : `${taskIndex + 1} / ${taskCount}`}
      </span>
    </div>
  );
}

type DetailSliderProps = {
  detailCount: number;
  detailIndex: number;
  onChange: (value: number) => void;
};

function DetailSlider({
  detailCount,
  detailIndex,
  onChange,
}: DetailSliderProps) {
  const disabled = detailCount === 0;
  const max = Math.max(detailCount - 1, 0);
  return (
    <div
      className={styles.detailSlider}
      data-test-role="plan-simulator-detail-slider"
    >
      <span className={styles.sliderLabel}>DETAIL</span>
      <div className={styles.detailTrackWrap}>
        <span className={styles.detailEndpoint}>
          {detailCount === 0 ? "—" : "1"}
        </span>
        <button
          type="button"
          className={styles.detailStepButton}
          aria-label="Previous detail"
          disabled={disabled || detailIndex <= 0}
          onClick={() => onChange(detailIndex - 1)}
        >
          <ChevronLeft size={16} />
        </button>
        <input
          type="range"
          className={styles.detailRange}
          aria-label="Detail"
          min={0}
          max={max}
          step={1}
          value={detailIndex}
          disabled={disabled}
          onChange={(e) => onChange(e.target.valueAsNumber)}
        />
        <button
          type="button"
          className={styles.detailStepButton}
          aria-label="Next detail"
          disabled={disabled || detailIndex >= max}
          onClick={() => onChange(detailIndex + 1)}
        >
          <ChevronRight size={16} />
        </button>
        <span className={styles.detailEndpoint}>
          {detailCount === 0 ? "—" : String(detailCount)}
        </span>
      </div>
      <span className={styles.sliderValue}>
        {detailCount === 0 ? "—" : `${detailIndex + 1} / ${detailCount}`}
      </span>
    </div>
  );
}

type ReadoutProps = {
  model: SimModel;
  activeTask?: SimTask;
  activePick?: SimPick;
  detailCount: number;
};

function computeStackValue(
  model: SimModel,
  activePick: SimPick | undefined,
): string {
  if (
    !activePick ||
    activePick.loadLocationKind !== "POSITIONED" ||
    activePick.row == null ||
    activePick.col == null
  ) {
    return "—";
  }
  const truck = model.trucks.find((t) => t.routeId === activePick.routeId);
  const cell = truck?.cells.find(
    (c) => c.row === activePick.row && c.col === activePick.col,
  );
  const total = cell?.stacks.length ?? 0;
  if (total <= 1) return "—";
  return `${activePick.stackPosition + 1} / ${total}`;
}

function Readout({ model, activeTask, activePick, detailCount }: ReadoutProps) {
  const taskValue = activeTask ? String(activeTask.taskId) : "—";
  const detailValue =
    activeTask && activePick
      ? `${activePick.detailOrder} / ${detailCount}`
      : "—";
  const routeValue = activePick?.routeId ?? "—";
  const loadLocationValue =
    activePick?.loadLocationKind === "POSITIONED" &&
    activePick.col != null &&
    activePick.row != null
      ? `(${activePick.col}, ${activePick.row})`
      : activePick?.loadLocationKind === "OVERFLOW"
        ? "OVFL"
        : "—";
  const slotValue = activePick?.slot ?? "—";
  const itemValue = activePick
    ? activePick.itemName
      ? `${activePick.itemName} (${activePick.itemId})`
      : activePick.itemId
    : "—";

  return (
    <div className={styles.readout} data-test-role="plan-simulator-header">
      <Field
        label="Task"
        value={taskValue}
        testRole="plan-simulator-header-task"
      />
      <Field
        label="Load Location"
        value={loadLocationValue}
        testRole="plan-simulator-header-load-location"
      />
      <Field
        label="Detail"
        value={detailValue}
        testRole="plan-simulator-header-detail"
      />
      <Field
        label="Slot"
        value={slotValue}
        testRole="plan-simulator-header-slot"
      />
      <Field
        label="Route"
        value={routeValue}
        testRole="plan-simulator-header-route"
      />
      <Field
        label="Stack"
        value={computeStackValue(model, activePick)}
        testRole="plan-simulator-header-stack"
      />
      <Field
        label="Item"
        value={itemValue}
        testRole="plan-simulator-header-item"
      />
      <Field
        label="Task ID"
        value={activeTask?.id ?? "—"}
        testRole="plan-simulator-header-task-id"
        mono
      />
      <Field
        label="Detail ID"
        value={activePick?.id ?? "—"}
        testRole="plan-simulator-header-detail-id"
        mono
      />
      <BarcodeField
        label="Slot UPC"
        value={activePick?.slotUpc}
        testRole="plan-simulator-header-slot-upc"
      />
      <BarcodeField
        label="Item UPC"
        value={activePick?.itemUpc}
        testRole="plan-simulator-header-item-upc"
      />
    </div>
  );
}

type FieldProps = {
  label: string;
  value: string;
  testRole: string;
  mono?: boolean;
};

function Field({ label, value, testRole, mono }: FieldProps) {
  return (
    <div className={styles.field} data-test-role={testRole}>
      <span className={styles.fieldLabel}>{label}</span>
      <span className={mono ? styles.fieldValueMono : styles.fieldValue}>
        {value}
      </span>
    </div>
  );
}

type BarcodeFieldProps = {
  label: string;
  value?: string;
  testRole: string;
};

/**
 * A readout field whose value is also rendered as a scannable barcode, so the
 * simulation can be driven with a real handheld scanner.
 */
function BarcodeField({ label, value, testRole }: BarcodeFieldProps) {
  return (
    <div
      className={clsx(styles.field, styles.barcodeField)}
      data-test-role={testRole}
    >
      <span className={styles.fieldLabel}>{label}</span>
      <span className={styles.fieldValueMono}>{value ?? "—"}</span>
      {value && <Barcode value={value} label={label} height={36} />}
    </div>
  );
}
